import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  styled,
  tooltipClasses,
  TextField,
  FormHelperText,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Delete, Edit } from "@mui/icons-material";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { setNotification } from "../../redux/slices/notificationSlice";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { CHECKROLE } from "../../utils/constants";

const WhiteTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    arrow
    placement="bottom"
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#FFFFFF",
    color: "#000000",
    fontSize: "14px",
    lineHeight: "20px",
    borderRadius: "16px",
    padding: "11px 32px",
    boxShadow: " 0px 0px 15px 0px #00000026",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#FFFFFF",
  },
}));

const TableComponent = ({
  headings = {},
  tableData = [],
  selectedTab,
  handleEdit,
  handleDelete,
  isAddingFop,
  // setIsAddingFop,
  setNewFopData,
  newFopData,
  handleAddFop,
  paymentOptions,
  fopAddingName,
  fopDetails,
  handleSaveFop,
  handleCancelFop,
  isAddingPos,
  setIsAddingPos,
  newPosData,
  handleAddPos,
  handleSavePos,
  posDetails,
  posOptions,
  setNewPosData,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [errorMessage, setErrorMessage] = useState("");
  const [newlyAddedRow, setNewlyAddedRow] = useState(null);
  const [check, setCheck] = useState(false);
  const [fopError, setFOPError] = useState({
    paymentCodeError: "",
    fopNameError: "",
  });

  const state = useSelector(
    ({
      seller: {
        msal: { roles },
      },
    }) => ({
      roles,
    })
  );

  const isSupportUser = state.roles.includes(CHECKROLE.SUPPORT_USER);

  const dispatch = useDispatch();

  React.useEffect(() => {
    handleCancelFop();
    setNewPosData({
      pos: "",
      countryName: "",
      date: "",
    });
    setIsAddingPos(false);
    setIsAddingPos(false);
    setErrorMessage("");
    setNewlyAddedRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const headingKeys = Object.keys(headings);
  const sortedData = [...tableData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = a[sortConfig.key] || "";
    const bValue = b[sortConfig.key] || "";

    if (sortConfig.key === "date") {
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);

      if (aDate < bDate) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aDate > bDate) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    }

    if (aValue < bValue) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }

    if (aValue > bValue) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }

    return 0;
  });

  const handlePaymentAddCheck = (e) => {
    const selectedValue = e.target.value;
    const selectedOption = paymentOptions.find(
      (option) => option.value === selectedValue
    );

    // Check if the paymentCode already exists in fopDetails
    const isDuplicate = fopDetails?.data?.some(
      (fop) => fop.paymentCode === selectedValue
    );

    if (isDuplicate) {
      setCheck(true);
      setErrorMessage("This FOP already exists.");
      dispatch(
        setNotification({
          message: "This FOP already exists.",
          severity: "error",
          open: true,
        })
      );
    } else {
      setCheck(false);
      setErrorMessage("");
    }
    setNewFopData((prev) => ({
      ...prev,
      payment: selectedValue,
      fopName: selectedOption?.label || "",
    }));
  };

  const sortedPos = [...posOptions].sort((a, b) => {
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });

  const handlePosAddCheck = (e) => {
    const selectedValue = e.target.value;

    const selectedOption = posOptions.find(
      (option) => option.value === selectedValue
    );

    // Check if the paymentCode already exists in posDetails
    const isDuplicate = posDetails?.data?.some(
      (pos) => pos.pos === selectedValue
    );

    if (isDuplicate) {
      setCheck(true);
      setErrorMessage("This POS already exists.");
      dispatch(
        setNotification({
          message: "This POS already exists.",
          severity: "error",
          open: true,
        })
      );
      // return
    } else {
      setCheck(false);
      setErrorMessage("");
    }
    setNewPosData((prev) => ({
      ...prev,
      pos: selectedValue,
      countryName: selectedOption?.label || "",
    }));
  };

  handleAddFop = () => {
    if (!newFopData.payment || !newFopData.fopName) {
      if (!newFopData.payment) {
        setFOPError((prev) => ({
          ...prev,
          paymentCodeError: "Payment Code is mandatory.",
        }));
      }
      if (!newFopData.fopName) {
        setFOPError((prev) => ({
          ...prev,
          fopNameError: "FOP Name is mandatory.",
        }));
      }
      return;
    } else if (fopError.fopNameError || fopError.paymentCodeError) {
      return;
    } else {
      setFOPError((prev) => ({
        ...prev,
        paymentCodeError: "",
        fopNameError: "",
      }));
      handleSaveFop();
      setErrorMessage("");
      setNewlyAddedRow(0); // Set the index of the newly added row
      setTimeout(() => setNewlyAddedRow(null), 10000); // Clear the index after 10 seconds
    }
  };

  handleAddPos = () => {
    if (!newPosData.pos) {
      setErrorMessage("Payment Code is mandatory.");
      return;
    } else if (check) {
      return;
    } else {
      handleSavePos();
      setErrorMessage("");
      setNewlyAddedRow(0);
      setTimeout(() => setNewlyAddedRow(null), 10000);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const formattedDate = (date) => {
    if (!date) return "";
    return moment(date).format("YYYY-MM-DD");
  };

  const dateChanges = (date) => {
    if (!date) return "";
    return moment(date).format("MMM DD, YYYY");
  };

  const validateAlphabetsOnly = (value) => {
    const alphabetRegex = /^[A-Za-z\s]*$/; // Only letters and spaces

    return alphabetRegex.test(value);
  };

  return (
    <>
      {tableData.length || isAddingFop || isAddingPos ? (
        <TableContainer
          sx={{
            border: "1px solid #C9CACC",

            overflow: "auto",

            scrollbarWidth: "thin",
          }}
        >
          <Table stickyHeader sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow sx={{ height: "84px" }}>
                {headingKeys.map((heading, index) => (
                  <TableCell key={index} sx={{ backgroundColor: "#F1F7FB" }}>
                    <Box display="flex" alignItems="center">
                      <div
                        style={{
                          fontFamily: "Open Sans",
                          fontWeight: 700,
                          fontSize: "16px",
                          lineHeight: "20px",
                          color: "#000000",
                        }}
                      >
                        {headings[heading]}
                      </div>
                      {heading === "fop" ? (
                        ""
                      ) : (
                        <SwapVertIcon
                          sx={{
                            Height: "17.4px",
                            width: "18.8px",
                            cursor: "pointer",
                          }}
                          onClick={() => handleSort(heading)}
                        />
                      )}
                    </Box>
                  </TableCell>
                ))}

                {selectedTab !== 3 ? (
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      right: 0,
                      zIndex: 2,
                      backgroundColor: "#F1F7FB",
                      fontFamily: "Open Sans",
                      fontWeight: 700,
                      color: "#000000",
                      fontSize: "14px",
                      lineHeight: "20px",
                      boxShadow: isMobile ? "0 2px 5px rgba(0,0,0,5)" : "none",
                    }}
                  >
                    Manage
                  </TableCell>
                ) : (
                  ""
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Add Row for FOP */}

              {(isAddingFop || isAddingPos) &&
                (selectedTab === 1 || selectedTab === 2) && (
                  <TableRow sx={{ backgroundColor: "#1460AA" }}>
                    {headingKeys.map((cell, cellIndex) => (
                      <TableCell key={cellIndex} sx={{ color: "white" }}>
                        {cell ===
                        (selectedTab === 1 ? "payment" : "countryCode") ? (
                          <>
                            <FormControl
                              variant="standard"
                              sx={{ width: "75%" }}
                            >
                              {selectedTab === 2 ? (
                                <InputLabel
                                  sx={{
                                    color: "white",
                                    "&.Mui-focused": {
                                      color: "white",
                                    },
                                  }}
                                >
                                  {selectedTab === 1
                                    ? "Payment Code"
                                    : "Point of sale"}
                                </InputLabel>
                              ) : null}
                              {selectedTab === 1 ? (
                                <>
                                  {" "}
                                  <TextField
                                    id="standard-basic"
                                    label="Payment Code"
                                    variant="standard"
                                    value={newFopData.payment}
                                    error={!!fopError.paymentCodeError}
                                    // helperText={fopError.paymentCodeError}
                                    onChange={(e) => {
                                      if (e.target.value.length < 3) {
                                        const error = validateAlphabetsOnly(
                                          e.target.value
                                        );
                                        console.log(error);
                                        if (!error) {
                                          setFOPError((prev) => ({
                                            ...prev,
                                            paymentCodeError:
                                              "Please enter exactly 2 uppercase letters for FOP Code",
                                          }));
                                        } else {
                                          setFOPError((prev) => ({
                                            ...prev,
                                            paymentCodeError: "",
                                          }));
                                        }
                                        // setFOP((prev) => ({
                                        //   ...prev,
                                        //   paymentCode: e.target.value
                                        //     .toUpperCase()
                                        //     .trim(),
                                        // }));
                                        setNewFopData((prev) => ({
                                          ...prev,
                                          payment: e.target.value
                                            .toUpperCase()
                                            .trim(),
                                        }));
                                      }
                                    }}
                                    InputLabelProps={{
                                      sx: {
                                        color: "white",
                                        "&.Mui-focused": {
                                          color: "white",
                                        },
                                        "&.Mui-error": {
                                          color: "white",
                                        },
                                      },
                                    }}
                                    sx={{
                                      color: "white",

                                      "& .MuiInput-underline:before": {
                                        borderBottomColor: "white", // Default border color
                                      },

                                      "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                                        {
                                          borderBottomColor: "white", // Hover border color
                                        },

                                      "& .MuiInput-underline:after": {
                                        borderBottomColor: "white", // Focus border color
                                      },

                                      "& .MuiInputBase-input": {
                                        color: "white", // Input text color
                                      },

                                      "& .MuiFormHelperText-root": {
                                        color: "white", // Error text color
                                      },
                                    }}
                                  />
                                  {fopError.paymentCodeError && (
                                    <FormHelperText
                                      sx={{
                                        color: "white",
                                        display: "flex",
                                        alignItems: "center",
                                        fontSize: "12px",
                                        mt: 1,
                                      }}
                                    >
                                      <ErrorIcon
                                        sx={{ fontSize: "16px", mr: "8px" }}
                                      />
                                      {fopError.paymentCodeError}
                                    </FormHelperText>
                                  )}
                                </>
                              ) : (
                                <Select
                                  value={
                                    selectedTab === 1
                                      ? newFopData.payment
                                      : newPosData.pos
                                  }
                                  onChange={(e) => {
                                    selectedTab === 1
                                      ? handlePaymentAddCheck(e)
                                      : handlePosAddCheck(e);
                                  }}
                                  displayEmpty
                                  renderValue={(selected) => {
                                    if (!selected) return ""; // Handle empty value
                                    return selected; // Display only the value after selection
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                      },
                                    },
                                    anchorOrigin: {
                                      vertical: "bottom",
                                      horizontal: "left",
                                    },
                                    transformOrigin: {
                                      vertical: "top",
                                      horizontal: "left",
                                    },
                                    getContentAnchorEl: null,
                                  }}
                                  sx={{
                                    color: "white",
                                    "&::before": {
                                      borderBottom: "1px solid #C9CACC",
                                    },
                                    "&::after": {
                                      borderBottom: "1px solid #C9CACC",
                                    },
                                    "& .MuiSvgIcon-root": {
                                      color: "white", // Arrow icon color
                                    },
                                  }}
                                >
                                  {(selectedTab === 1
                                    ? paymentOptions
                                    : sortedPos
                                  ).map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {selectedTab === 2
                                        ? `${option.value} - ${option.label}`
                                        : option.value}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            </FormControl>
                            {errorMessage && (
                              <Typography
                                sx={{
                                  fontFamily: "Open Sans",
                                  fontWeight: 400,
                                  fontSize: "12px",
                                  lineHeight: "16px",
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#FFFFFF",
                                  mt: 1,
                                }}
                              >
                                <ErrorIcon
                                  sx={{ fontSize: "16px", mr: "10px" }}
                                />
                                {errorMessage}
                              </Typography>
                            )}
                          </>
                        ) : cell ===
                          (selectedTab === 1 ? "fopName" : "country") ? (
                          selectedTab === 1 ? (
                            <>
                              {" "}
                              <TextField
                                id="standard-basic"
                                label="FOP Name"
                                variant="standard"
                                value={newFopData.fopName}
                                error={!!fopError.fopNameError}
                                // helperText={fopError.paymentCodeError}
                                onChange={(e) => {
                                  const error = validateAlphabetsOnly(
                                    e.target.value
                                  );

                                  if (!error) {
                                    setFOPError((prev) => ({
                                      ...prev,
                                      fopNameError:
                                        "Please enter a valid FOP Name without numbers or special characters.",
                                    }));
                                  } else {
                                    setFOPError((prev) => ({
                                      ...prev,
                                      fopNameError: "",
                                    }));
                                  }
                                  // setFOP((prev) => ({
                                  //   ...prev,
                                  //   fopName: e.target.value,
                                  // }));
                                  setNewFopData((prev) => ({
                                    ...prev,
                                    fopName: e.target.value,
                                  }));
                                }}
                                InputLabelProps={{
                                  sx: {
                                    color: "white",
                                    "&.Mui-focused": {
                                      color: "white",
                                    },
                                    "&.Mui-error": {
                                      color: "white",
                                    },
                                  },
                                }}
                                sx={{
                                  color: "white",

                                  "& .MuiInput-underline:before": {
                                    borderBottomColor: "white", // Default border color
                                  },

                                  "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                                    {
                                      borderBottomColor: "white", // Hover border color
                                    },

                                  "& .MuiInput-underline:after": {
                                    borderBottomColor: "white", // Focus border color
                                  },

                                  "& .MuiInputBase-input": {
                                    color: "white", // Input text color
                                  },

                                  "& .MuiFormHelperText-root": {
                                    color: "white", // Error text color
                                  },
                                }}
                              />
                              {fopError.fopNameError && (
                                <FormHelperText
                                  sx={{
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "12px",
                                    mt: 1,
                                  }}
                                >
                                  <ErrorIcon
                                    sx={{ fontSize: "16px", mr: "8px" }}
                                  />
                                  {fopError.fopNameError}
                                </FormHelperText>
                              )}
                            </>
                          ) : (
                            "-"
                          )
                        ) : cell === "date" ? (
                          <>
                            {newFopData.date}
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontFamily: "Open Sans",
                                fontSize: "14px",
                                lineHeight: "20px",
                                color: "white",
                              }}
                            >
                              Adding by {fopAddingName}
                            </Typography>
                          </>
                        ) : (
                          ""
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center">
                        <IconButton
                          onClick={(e) =>
                            selectedTab === 1
                              ? handleAddFop(e)
                              : handleAddPos(e)
                          }
                        >
                          <AddCircleIcon
                            sx={{
                              width: "20px",
                              height: "20px",
                              color: "white",
                            }}
                          />
                          <span
                            style={{
                              color: "white",
                              fontWeight: 700,
                              fontSize: "16px",
                              lineHeight: "20px",
                              fontFamily: "Open Sans",
                              marginLeft: "10px",
                            }}
                          >
                            Add
                          </span>
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

              {/* Render Table Rows */}

              {sortedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  sx={{
                    border:
                      newlyAddedRow === rowIndex
                        ? "2px solid #1460AA !important"
                        : "auto", // Apply blue border

                    transition: "border 0.3s ease-in-out", // Smooth transition
                    "& > *": {
                      // borderTop: 'none',
                      borderTop:
                        newlyAddedRow === rowIndex
                          ? "2px solid #1460AA !important"
                          : "1px solid #C9CACC", // Force blue border on top
                      borderBottom:
                        newlyAddedRow === rowIndex
                          ? "2px solid #1460AA"
                          : "auto",
                    },
                  }}
                >
                  {headingKeys.map((cell, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Typography
                        sx={{
                          fontWeight: cell === "status" ? 700 : 400,
                          fontFamily: "Open Sans",
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: "#000000",
                        }}
                      >
                        {cell === "date" ? (
                          <span>
                            {selectedTab === 0 || selectedTab === 3
                              ? formattedDate(row[cell])
                              : dateChanges(row[cell])}
                            <Typography
                              sx={{
                                fontWeight: 400,

                                fontFamily: "Open Sans",

                                fontSize: "14px",
                              }}
                            >
                              {row.action}{" "}
                              {selectedTab !== 3 ? `By ${row.name}.` : ""}
                            </Typography>
                          </span>
                        ) : cell === "fop" ? (
                          row.fop === "" ? (
                            "N/A"
                          ) : (
                            row.fop.split(",").map((fopCode, index) => {
                              const eligibleDetail = row.eligibleFOPDetail
                                .split(",")
                                .find((item) => item.startsWith(fopCode + ":"));
                              const tooltipText = eligibleDetail
                                ? eligibleDetail.split(":")[1]
                                : "";

                              return (
                                <WhiteTooltip
                                  key={index}
                                  title={tooltipText}
                                  arrow
                                  sx={{ bgcolor: "#fff" }}
                                >
                                  <span style={{ marginRight: 4 }}>
                                    {fopCode}
                                    {index < row.fop.split(",").length - 1 &&
                                      ","}
                                  </span>
                                </WhiteTooltip>
                              );
                            })
                          )
                        ) : cell === "log" && Array.isArray(row[cell]) ? (
                          <ul style={{ marginLeft: "-10px" }}>
                            {row[cell].map((logItem, index) => (
                              <li key={index}>{logItem}</li>
                            ))}
                          </ul>
                        ) : (
                          row[cell]
                        )}
                      </Typography>
                    </TableCell>
                  ))}

                  {selectedTab !== 3 ? (
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                        backgroundColor: "white",
                        borderRight: "1px grey",
                        boxShadow: "0 2px 5px rgba(0,0,0,1)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                        }}
                      >
                        {selectedTab === 0 ? (
                          <IconButton onClick={() => handleEdit(row)}>
                            <Edit sx={{ color: "#000000" }} />
                          </IconButton>
                        ) : (
                          ""
                        )}
                        <Box
                          position="relative"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{ ml: selectedTab !== 0 ? -4.5 : 0 }}
                        >
                          {/* change color and remove disabled when post and pu api integrated */}
                          <IconButton
                            onClick={() => {
                              if (!isSupportUser) {
                                handleDelete(row.id);
                              }
                            }}
                          >
                            <Delete
                              sx={{
                                width: "24px",
                                height: "24px",
                                cursor: "pointer",
                                // color: "#000000",
                                color: !isSupportUser
                                  ? "rgba(0, 0, 0, 0.92)"
                                  : "gray",
                              }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    </TableCell>
                  ) : (
                    ""
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <Typography sx={{ color: "#000000", fontWeight: 700 }}>
            No data
          </Typography>
        </Box>
      )}
    </>
  );
};

export default TableComponent;
